// styles.js - Centralized styles for the notification system

export const colors = {
  primary: '#5968b7',
  primaryHover: '#4a5a9e',
  background: '#fff',
  backgroundSecondary: '#f8f9ff',
  backgroundTertiary: 'rgba(239, 239, 239, 0.5)',
  border: '#5968b7',
  text: '#666',
  textSecondary: 'textSecondary',
  
  // Status colors
  status: {
    active: '#ff9800',
    resolved: '#4caf50',
    pending: '#2196f3',
    default: '#757575'
  },
  
  // Severity colors
  severity: {
    critical: '#f44336',
    high: '#ff9800',
    medium: '#2196f3',
    low: '#4caf50',
    default: '#757575'
  },
  
  // Type colors
  type: {
    COMMUNICATION_ERROR: '#e91e63',
    SYSTEM_ALERT: '#ff5722',
    MAINTENANCE: '#607d8b',
    PERFORMANCE: '#9c27b0',
    default: '#757575'
  },
  
  // Email status colors
  email: {
    sent: '#4caf50',
    notSent: '#ff5722'
  },
  
  // Processing status colors
  processing: {
    processed: '#4caf50',
    notProcessed: '#ff9800'
  }
};

export const commonStyles = {
  // Search input styles
  searchInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '25px',
      backgroundColor: colors.backgroundTertiary,
      border: `1px solid ${colors.border}`,
    },
  },
  
  // Date picker styles
  datePicker: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '25px',
      backgroundColor: colors.background,
      boxShadow: '1px 4px 15px rgba(0, 0, 0, 0.05)',
    },
  },
  
  // Select dropdown styles
  selectDropdown: {
    borderRadius: '25px',
    backgroundColor: colors.background,
    boxShadow: '1px 4px 15px rgba(0, 0, 0, 0.05)',
  },
  
  // Button styles
  primaryButton: {
    height: '40px',
    backgroundColor: colors.background,
    borderRadius: '25px',
    boxShadow: '1px 4px 15px rgba(0, 0, 0, 0.05)',
    color: colors.primary,
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: colors.primary,
      color: 'white',
      transform: 'translateY(-1px)',
      boxShadow: '1px 6px 20px rgba(0, 0, 0, 0.15)',
    },
  },
  
  viewButton: {
    borderRadius: '20px',
    textTransform: 'none',
    backgroundColor: colors.primary,
    '&:hover': {
      backgroundColor: colors.primaryHover,
    },
  },
  
  closeButton: {
    borderRadius: '20px',
    textTransform: 'none',
    backgroundColor: colors.primary,
    color: 'white',
    '&:hover': {
      backgroundColor: colors.primaryHover,
    },
  },
  
  // Chip styles
  chip: {
    fontWeight: 'bold',
    color: 'white',
  },
  
  typeChip: {
    ...this?.chip,
    fontSize: '11px',
  },
  
  // Modal styles
  modalPaper: {
    borderRadius: '12px',
    maxHeight: '80vh',
  },
  
  modalTitle: {
    m: 0,
    p: 2,
    backgroundColor: colors.backgroundTertiary,
    color: 'black',
  },
  
  modalCloseButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: 'white',
  },
};

export const tableStyles = {
  container: {
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
    marginTop: '20px',
  },
  
  root: {
    '& .main_table table': {
      borderCollapse: 'separate',
      borderSpacing: '0 10px',
      border: 'none',
      backgroundColor: 'transparent',
    },
    '& th': {
      border: 'none',
      color: colors.primary,
      borderBottom: `0.25px solid ${colors.primary}`,
      fontSize: '0.688rem',
      fontWeight: 500,
    },
    '& td': {
      border: 'none',
      backgroundColor: colors.background,
    },
    '& td:first-child': {
      borderRadius: '6px 0px 0px 6px',
    },
    '& td:last-child': {
      borderRadius: '0px 6px 6px 0px',
    },
    '& .MuiTableContainer-root': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    '& .MuiTableRow-root:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease-in-out',
      '& td': {
        backgroundColor: `${colors.backgroundSecondary} !important`,
      },
    },
  },
  
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: colors.background,
  },
  
  messageCell: {
    maxWidth: '300px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  
  dateCell: {
    fontSize: '12px',
    color: colors.text,
  },
  
  emptyRow: {
    py: 3,
  },
};

export const layoutStyles = {
  container: {
    padding: '20px',
  },
  
  header: {
    '& .MuiTextField-root': {
      m: 1,
      width: '100%',
    },
  },
  
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '48px',
  },
  
  searchWrapper: {
    width: '80%',
  },
  
  filtersContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
  },
  
  datePickerWrapper: {
    width: '140px',
    marginRight: '8px',
  },
  
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  modalContent: {
    mt: 2,
  },
  
  modalActions: {
    p: 2,
  },
  
  detailBox: {
    mb: 2,
  },
  
  emailStatusContainer: {
    marginBottom: '8px',
  },
};

// Utility functions for getting colors
export const getStatusColor = (status) => {
  return colors.status[status?.toLowerCase()] || colors.status.default;
};

export const getSeverityColor = (severity) => {
  return colors.severity[severity?.toLowerCase()] || colors.severity.default;
};

export const getTypeColor = (type) => {
  return colors.type[type] || colors.type.default;
};

export const getEmailColor = (emailSent) => {
  return emailSent ? colors.email.sent : colors.email.notSent;
};

export const getProcessingColor = (processed) => {
  return processed ? colors.processing.processed : colors.processing.notProcessed;
};